import { compact } from 'lodash-es';
import { LP_SETTLEMENT_TABS } from './LpSettlement/lpSettlementTabs';
import {
  getPortfolioLayoutOptions,
  getPortfolioLiteLayoutOptions,
} from './PortfolioManagement/layoutConfiguration/getPortfolioLayoutOptions';

/**
 * Gets the label for an LP Settlement sub-tab route by looking it up in the TABS definition
 */
function getLpSettlementSubTabLabel(route: string): string | undefined {
  const tab = LP_SETTLEMENT_TABS.find(t => t.route === route);
  return tab?.label;
}

/**
 * Resolves Portfolio breadcrumb labels using the layout options configuration.
 * Tries both full and lite layout options to find matching routes.
 *
 * @param path - The pathname to resolve (e.g., '/portfolio/operations/lp-settlement/active-batches')
 * @returns Array of breadcrumb labels, or null if not a Portfolio route
 */
export function resolvePortfolioBreadcrumb(path: string): (string | undefined)[] | null {
  const pathSegments = path.split('/').filter(Boolean);
  if (pathSegments.length < 2 || pathSegments[0] !== 'portfolio') {
    return null;
  }

  const viewRoute = pathSegments[1]; // e.g., "operations"
  const tabRoute = pathSegments[2]; // e.g., "lp-settlement"
  const subTabRoute = pathSegments[3]; // e.g., "active-batches" (for LP Settlement)

  // Try both layout options to find the matching view and tab
  const layoutOptionsList = [getPortfolioLayoutOptions(), getPortfolioLiteLayoutOptions()];

  for (const layoutOptions of layoutOptionsList) {
    const view = layoutOptions.find(option => option.route === `/${viewRoute}`);
    if (!view) {
      continue;
    }

    const parts: (string | undefined)[] = ['portfolio', view.label];

    // If there's a tab route, try to find it in the view's tabs
    if (tabRoute) {
      const tab = view.tabs.find(t => t.route === `/${tabRoute}`);
      if (tab) {
        parts.push(tab.label);

        // For LP Settlement, check if there's a sub-tab route
        if (tabRoute === 'lp-settlement' && subTabRoute) {
          const subTabLabel = getLpSettlementSubTabLabel(subTabRoute);
          if (subTabLabel) {
            parts.push(subTabLabel);
          } else {
            parts.push(subTabRoute);
          }
        }
      } else {
        // Tab not found in layout options, use route segment as fallback
        parts.push(tabRoute);
      }
    }

    return compact(parts);
  }

  return null;
}
